/**
 * Trimmed YouTube video id — join key for `livestream_streams.videoId` and
 * `private_content.platformPostId`.
 */
export function normalizeYoutubeVideoId(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "";
  return String(raw).trim();
}

/** Parse YouTube video id from watch, live, share, or shorts URLs. */
export function youtubeVideoIdFromUrl(
  url: string | null | undefined,
): string | null {
  if (typeof url !== "string" || !url.trim()) return null;
  const u = url.trim();
  const watch = u.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  if (watch?.[1]) return watch[1];
  const short = u.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
  if (short?.[1]) return short[1];
  const live = u.match(/youtube\.com\/live\/([A-Za-z0-9_-]{11})/);
  if (live?.[1]) return live[1];
  const shorts = u.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/);
  if (shorts?.[1]) return shorts[1];
  const embed = u.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/);
  if (embed?.[1]) return embed[1];
  return null;
}

/**
 * YouTube video id from private/catalog content (long-form uses `platformPostId` or `url` `v=`).
 */
export function getYoutubeVideoIdFromContent(content: {
  source?: string;
  platformPostId?: string;
  url?: string;
} | null | undefined): string | null {
  if (!content || content.source !== "youtube") return null;
  const fromPost = normalizeYoutubeVideoId(content.platformPostId ?? undefined);
  if (fromPost) return fromPost;
  return youtubeVideoIdFromUrl(content.url);
}

/** Embed URL for in-app YouTube preview at a given start time (seconds). */
export function youtubeEmbedUrlFromWatchUrl(
  url: string | null | undefined,
  startTimeSec: number,
): string | null {
  const videoId = youtubeVideoIdFromUrl(url);
  if (!videoId) return null;
  const start = Math.max(0, Math.floor(startTimeSec));
  return `https://www.youtube.com/embed/${videoId}?start=${start}&rel=0&modestbranding=1`;
}

/** Watch URL with timestamp — works when embedding is blocked by the rights holder. */
export function youtubeWatchUrlFromWatchUrl(
  url: string | null | undefined,
  startTimeSec: number,
): string | null {
  const videoId = youtubeVideoIdFromUrl(url);
  if (!videoId) return null;
  const start = Math.max(0, Math.floor(startTimeSec));
  return `https://www.youtube.com/watch?v=${videoId}&t=${start}s`;
}

/** Static thumbnail for a YouTube watch URL (hqdefault = 480×360). */
export function youtubeThumbnailUrlFromWatchUrl(
  url: string | null | undefined,
  quality: "default" | "mqdefault" | "hqdefault" | "sddefault" = "hqdefault",
): string | null {
  const videoId = youtubeVideoIdFromUrl(url);
  if (!videoId) return null;
  return `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`;
}
